/**
 * useNow — single shared 1s clock for countdown UIs.
 *
 * One module-level setInterval feeds every subscriber so 30 game rows
 * with countdown badges don't spawn 30 timers (and 30 setStates fanning
 * out at slightly skewed phases). The interval is lazy: it only ticks
 * while at least one component is mounted using the hook.
 *
 * Returns `Date.now()` as of the most recent tick; components using it
 * re-render whenever the shared `_now` value advances.
 */

import { useSyncExternalStore } from 'react';

let _now = Date.now();
const _listeners = new Set<() => void>();
let _intervalId: ReturnType<typeof setInterval> | null = null;

function tick(): void {
  _now = Date.now();
  for (const l of _listeners) l();
}

function ensureRunning(): void {
  if (_intervalId == null) {
    _now = Date.now();
    _intervalId = setInterval(tick, 1000);
  }
}

function maybeStop(): void {
  if (_intervalId != null && _listeners.size === 0) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
}

const subscribe = (cb: () => void): (() => void) => {
  _listeners.add(cb);
  ensureRunning();
  return () => {
    _listeners.delete(cb);
    maybeStop();
  };
};

const getSnapshot = (): number => _now;

export function useNow(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
